/**
 * MatchDayMode - Real-time match day coaching interface
 * 
 * Mobile-first design for touchline use during games.
 * Quick event logging, live score, simplified squad view, and substitution panel.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FORMATION_POSITIONS, FORMATIONS_BY_FORMAT } from '../components/match/constants';
import {
  ArrowLeft, Save, Minus, Plus, CircleDot, Square, ArrowLeftRight,
  Timer, ChevronUp, ChevronDown, StopCircle, Mic, MicOff, Loader2, Check, X as XIcon, Pencil, Medal, Layers,
  RefreshCw, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveBadgeUrl = (badge) => {
  if (!badge) return null;
  if (badge.startsWith('http://') || badge.startsWith('https://')) return badge;
  return `${BACKEND_URL}${badge}`;
};

// Light haptic tap — used on the high-frequency match-day controls so coaches
// get tactile confirmation when their thumb hits the right button without
// looking. Silently no-ops on devices/browsers without the Vibration API
// (Safari iOS, desktop). 10ms is the same value used on the onboarding Next
// button and the matchday back button — short enough to feel like a "tick".
const tapHaptic = () => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(10);
    }
  } catch (_) { /* ignore — read-only on some platforms */ }
};

import { useVoiceCapture } from '../hooks/useVoiceCapture';

// Quick-event definitions
// Quick-event definitions removed — the new sticky-header action row hard-codes
// the 5 buttons (GOAL · CARD · SUB · VAR · END). Existing event_type IDs are
// still 'goal' | 'yellow_card' | 'red_card' | 'substitution' under the hood.

export default function MatchDayMode() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [events, setEvents] = useState([]);
  const [opponentBadge, setOpponentBadge] = useState(null);
  const [ourScore, setOurScore] = useState(0);
  const [oppScore, setOppScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Event logging state
  const [activeEvent, setActiveEvent] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [assistPlayer, setAssistPlayer] = useState(null);
  const [subOffPlayer, setSubOffPlayer] = useState(null);
  const [eventMinute, setEventMinute] = useState('');
  const [isOpponentEvent, setIsOpponentEvent] = useState(false);
  const [isOwnGoal, setIsOwnGoal] = useState(false);
  const [isPenalty, setIsPenalty] = useState(false);
  const [penaltyConcededBy, setPenaltyConcededBy] = useState(null);
  const [goalType, setGoalType] = useState(null); // penalty, shot_inside, shot_outside, header, own_goal
  const [showTimeline, setShowTimeline] = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [deleteEventIndex, setDeleteEventIndex] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showPotmSelect, setShowPotmSelect] = useState(false);
  const [selectedPotm, setSelectedPotm] = useState(null);
  const [showFormationPicker, setShowFormationPicker] = useState(false);
  const [showTimerConfirm, setShowTimerConfirm] = useState(false);
  const [showTimerMenu, setShowTimerMenu] = useState(false);
  const [showShootout, setShowShootout] = useState(false);
  const [penalties, setPenalties] = useState([]); // [{ team: 'home'|'away', player_id: null, result: 'scored'|'missed' }]
  const [penShootoutPlayer, setPenShootoutPlayer] = useState(null);

  // Timer state
  const [timerMinutes, setTimerMinutes] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [matchPeriod, setMatchPeriod] = useState('pre');
  const timerIntervalRef = useRef(null);
  const timerStartRef = useRef(null); // epoch ms when timer was started
  const timerBaseRef = useRef(0); // base minutes when timer was started (0 or 45)

  // Position swap state
  const [swapSource, setSwapSource] = useState(null);
  const pitchRef = useRef(null);

  // Voice recognition state
  const [voiceParsing, setVoiceParsing] = useState(false);
  const [voiceResult, setVoiceResult] = useState(null); // parsed event preview
  const [voiceTranscript, setVoiceTranscript] = useState('');

  const minuteRef = useRef(null);

  // Helper: compute elapsed time from a stored start timestamp
  const computeElapsed = useCallback((startMs, baseMin) => {
    const elapsed = Math.floor((Date.now() - startMs) / 1000);
    const totalSeconds = baseMin * 60 + elapsed;
    return { minutes: Math.floor(totalSeconds / 60), seconds: totalSeconds % 60 };
  }, []);

  // Refs for timer values to avoid dependency cascades
  const timerMinutesRef = useRef(0);
  const timerSecondsRef = useRef(0);
  const matchPeriodRef = useRef('pre');

  // Helper: save timer state to localStorage
  const persistTimer = useCallback((running, startMs, baseMin, period) => {
    const key = `matchday_timer_${matchId}`;
    if (running && startMs) {
      localStorage.setItem(key, JSON.stringify({ startMs, baseMin, period }));
    } else {
      localStorage.setItem(key, JSON.stringify({ startMs: null, baseMin, period, stoppedMin: timerMinutesRef.current, stoppedSec: timerSecondsRef.current }));
    }
  }, [matchId]);

  // Start a live tick interval that computes from the start timestamp
  const beginTick = useCallback((startMs, baseMin) => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    timerStartRef.current = startMs;
    timerBaseRef.current = baseMin;
    const tick = () => {
      const { minutes, seconds } = computeElapsed(startMs, baseMin);
      timerMinutesRef.current = minutes;
      timerSecondsRef.current = seconds;
      setTimerMinutes(minutes);
      setTimerSeconds(seconds);
    };
    tick();
    timerIntervalRef.current = setInterval(tick, 1000);
    setTimerRunning(true);
  }, [computeElapsed]);

  const startTimer = useCallback(() => {
    if (timerRunning) return;
    const startMs = Date.now();
    const baseMin = timerMinutesRef.current;
    timerStartRef.current = startMs;
    timerBaseRef.current = baseMin;
    beginTick(startMs, baseMin);
    persistTimer(true, startMs, baseMin, matchPeriodRef.current);
  }, [timerRunning, beginTick, persistTimer]);

  const stopTimer = useCallback(() => {
    setTimerRunning(false);
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    persistTimer(false, null, 0, matchPeriodRef.current);
  }, [persistTimer]);

  const toggleTimer = useCallback(() => {
    if (timerRunning) stopTimer();
    else startTimer();
  }, [timerRunning, startTimer, stopTimer]);

  const setMatchPeriodByVoice = useCallback((period) => {
    const halfLen = match?.half_length || user?.half_length || 45;
    const etHalfLen = match?.et_half_length || user?.et_half_length || 15;
    matchPeriodRef.current = period;
    if (period === 'first_half') {
      setMatchPeriod('first_half');
      setTimerMinutes(0);
      setTimerSeconds(0);
      timerMinutesRef.current = 0;
      timerSecondsRef.current = 0;
      const startMs = Date.now();
      beginTick(startMs, 0);
      persistTimer(true, startMs, 0, 'first_half');
    } else if (period === 'half_time') {
      setMatchPeriod('half_time');
      setTimerRunning(false);
      if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
      persistTimer(false, null, 0, 'half_time');
    } else if (period === 'second_half') {
      setMatchPeriod('second_half');
      setTimerMinutes(halfLen);
      setTimerSeconds(0);
      timerMinutesRef.current = halfLen;
      timerSecondsRef.current = 0;
      const startMs = Date.now();
      beginTick(startMs, halfLen);
      persistTimer(true, startMs, halfLen, 'second_half');
    } else if (period === 'full_time') {
      setMatchPeriod('full_time');
      setTimerRunning(false);
      if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
      persistTimer(false, null, 0, 'full_time');
    } else if (period === 'et_first') {
      // Extra-time 1st half — clock continues from end of 2nd half (2 × halfLen)
      const startMin = 2 * halfLen;
      setMatchPeriod('et_first');
      setTimerMinutes(startMin);
      setTimerSeconds(0);
      timerMinutesRef.current = startMin;
      timerSecondsRef.current = 0;
      const startMs = Date.now();
      beginTick(startMs, startMin);
      persistTimer(true, startMs, startMin, 'et_first');
    } else if (period === 'et_half_time') {
      setMatchPeriod('et_half_time');
      setTimerRunning(false);
      if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
      persistTimer(false, null, 0, 'et_half_time');
    } else if (period === 'et_second') {
      // ET 2nd half — clock continues from (2 × halfLen + etHalfLen)
      const startMin = 2 * halfLen + etHalfLen;
      setMatchPeriod('et_second');
      setTimerMinutes(startMin);
      setTimerSeconds(0);
      timerMinutesRef.current = startMin;
      timerSecondsRef.current = 0;
      const startMs = Date.now();
      beginTick(startMs, startMin);
      persistTimer(true, startMs, startMin, 'et_second');
    } else if (period === 'penalty') {
      // Shootout — clock stops, score tracked via pen-shootout panel
      setMatchPeriod('penalty');
      setTimerRunning(false);
      if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
      persistTimer(false, null, 0, 'penalty');
      setShowShootout(true);
    }
  }, [match, user, beginTick, persistTimer]);

  // Restore timer on mount
  useEffect(() => {
    const key = `matchday_timer_${matchId}`;
    const saved = localStorage.getItem(key);
    if (!saved) return;
    try {
      const data = JSON.parse(saved);
      if (data.period) setMatchPeriod(data.period);
      if (data.startMs) {
        // Timer was running — resume it
        beginTick(data.startMs, data.baseMin);
      } else if (data.stoppedMin !== undefined) {
        // Timer was stopped — restore last values
        setTimerMinutes(data.stoppedMin);
        setTimerSeconds(data.stoppedSec || 0);
      }
    } catch (e) { /* ignore corrupt data */ }
  }, [matchId, beginTick]);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, []);

  const handleVoiceResult = useCallback(async (text) => {
    setVoiceTranscript(text);

    // Check for timer voice commands first (no API call needed)
    const lower = text.toLowerCase();
    if (lower.includes('first half') && (lower.includes('start') || lower.includes('kick'))) {
      setMatchPeriodByVoice('first_half');
      toast.success('First half started');
      return;
    }
    if (lower.includes('first half') && (lower.includes('end') || lower.includes('over') || lower.includes('stop'))) {
      setMatchPeriodByVoice('half_time');
      toast.success('Half time');
      return;
    }
    if ((lower.includes('half time') || lower.includes('halftime'))) {
      setMatchPeriodByVoice('half_time');
      toast.success('Half time');
      return;
    }
    if (lower.includes('second half') && (lower.includes('start') || lower.includes('kick'))) {
      setMatchPeriodByVoice('second_half');
      toast.success('Second half started');
      return;
    }
    if (lower.includes('second half') && (lower.includes('end') || lower.includes('over') || lower.includes('stop'))) {
      setMatchPeriodByVoice('full_time');
      toast.success('Full time');
      return;
    }
    if (lower.includes('full time') || lower.includes('fulltime') || lower.includes('final whistle')) {
      setMatchPeriodByVoice('full_time');
      toast.success('Full time');
      return;
    }

    setVoiceParsing(true);
    try {
      const token = localStorage.getItem('token');
      // Build player list for AI context
      const activeIds = [...(match?.lineup || []), ...(match?.subs || []).map(s => s?.playerId)].filter(Boolean);
      const playerList = activeIds.map(pid => {
        const p = players.find(pl => pl.id === pid);
        if (!p) return null;
        const posIndex = match?.lineup?.indexOf(pid);
        const number = posIndex >= 0
          ? (match?.lineup_numbers?.[posIndex] || p.jersey_number || '')
          : (match?.sub_numbers?.[match?.subs?.findIndex(s => s?.playerId === pid)] || p.jersey_number || '');
        return { id: p.id, name: p.name, number };
      }).filter(Boolean);

      const res = await axios.post(`${API}/matches/parse-voice-event`, {
        transcript: text,
        players: playerList
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.error) {
        toast.error(res.data.error);
        setVoiceParsing(false);
        return;
      }
      // If AI didn't return a minute, use timer if active
      const result = { ...res.data };
      if (!result.minute && (timerRunning || timerMinutes > 0)) {
        result.minute = timerMinutes;
      }
      setVoiceResult(result);
    } catch (err) {
      toast.error('Failed to process voice command');
    } finally {
      setVoiceParsing(false);
    }
  }, [match, players, setMatchPeriodByVoice, timerRunning, timerMinutes]);

  const handleVoiceError = useCallback((msg) => {
    toast.error(msg);
  }, []);

  const voice = useVoiceCapture({
    engine: user?.voice_engine || 'webspeech',
    onResult: handleVoiceResult,
    onError: handleVoiceError,
    prompt: 'Soccer match commentary: goals, assists, cards, substitutions, position swaps with player names and numbers.',
  });
  const { isListening, isSupported, stop: stopListening } = voice;
  const startListening = voice.startAutoCapture;

  // Confirm voice-parsed event → add to timeline
  const confirmVoiceEvent = () => {
    if (!voiceResult) return;
    const v = voiceResult;
    if (!v.minute && v.minute !== 0) {
      toast.error('No time detected. Please enter the minute for this event.');
      return;
    }
    const min = v.minute;
    const newEvents = [];

    if (v.event_type === 'goal') {
      if (v.is_opponent) {
        newEvents.push({ event_type: 'goal', is_opponent: true, minute: min });
        setOppScore(prev => prev + 1);
      } else {
        newEvents.push({ event_type: 'goal', player_id: v.player_id, minute: min });
        if (v.assist_player_id) {
          newEvents.push({ event_type: 'assist', player_id: v.assist_player_id, minute: min });
        }
        setOurScore(prev => prev + 1);
      }
    } else if (v.event_type === 'yellow_card') {
      if (v.is_opponent) {
        newEvents.push({ event_type: 'yellow_card', is_opponent: true, minute: min });
      } else {
        newEvents.push({ event_type: 'yellow_card', player_id: v.player_id, minute: min });
      }
    } else if (v.event_type === 'red_card') {
      if (v.is_opponent) {
        newEvents.push({ event_type: 'red_card', is_opponent: true, minute: min });
      } else {
        newEvents.push({ event_type: 'red_card', player_id: v.player_id, minute: min });
      }
    } else if (v.event_type === 'substitution') {
      if (v.sub_off_id && v.sub_on_id) {
        newEvents.push({ event_type: 'substitution_out', player_id: v.sub_off_id, minute: min });
        newEvents.push({ event_type: 'substitution_in', player_id: v.sub_on_id, minute: min });
      }
    } else if (v.event_type === 'position_swap') {
      // Find lineup indices for both players and swap
      if (v.swap_player1_id && v.swap_player2_id && match?.lineup) {
        const idx1 = match.lineup.indexOf(v.swap_player1_id);
        const idx2 = match.lineup.indexOf(v.swap_player2_id);
        if (idx1 >= 0 && idx2 >= 0) {
          swapPositions(idx1, idx2);
          toast.success('Positions swapped');
          setVoiceResult(null);
          setVoiceTranscript('');
          return;
        }
      }
      toast.error('Could not find both players to swap');
      setVoiceResult(null);
      setVoiceTranscript('');
      return;
    }

    if (newEvents.length > 0) {
      setEvents(prev => [...prev, ...newEvents].sort((a, b) => a.minute - b.minute));
      setHasUnsavedChanges(true);
      toast.success('Voice event added');
    } else {
      toast.error('Could not create event from voice input');
    }
    setVoiceResult(null);
    setVoiceTranscript('');
  };

  const dismissVoiceResult = () => {
    setVoiceResult(null);
    setVoiceTranscript('');
  };

  // Edit event: populate edit state from an existing event
  const startEditEvent = (idx) => {
    const event = events[idx];
    if (!event) return;

    if (event.event_type === 'goal') {
      const assistEvt = !event.is_opponent
        ? events.find(e => e.event_type === 'assist' && e.minute === event.minute)
        : null;
      setEditingEvent({
        index: idx,
        event_type: 'goal',
        player_id: event.player_id || null,
        minute: event.minute,
        is_opponent: !!event.is_opponent,
        assist_player_id: assistEvt?.player_id || null,
      });
    } else if (event.event_type === 'yellow_card' || event.event_type === 'red_card') {
      setEditingEvent({
        index: idx,
        event_type: event.event_type,
        player_id: event.player_id || null,
        minute: event.minute,
        is_opponent: !!event.is_opponent,
      });
    } else if (event.event_type === 'substitution_in') {
      const subOutEvt = events.find(e => e.event_type === 'substitution_out' && e.minute === event.minute);
      setEditingEvent({
        index: idx,
        event_type: 'substitution',
        sub_on_id: event.player_id,
        sub_off_id: subOutEvt?.player_id || null,
        minute: event.minute,
      });
    }
  };

  const saveEditEvent = () => {
    if (!editingEvent) return;
    const e = editingEvent;
    if (!e.minute && e.minute !== 0) { toast.error('Enter the minute'); return; }
    const min = parseInt(e.minute);

    const newEvents = [...events];

    if (e.event_type === 'goal') {
      // Update the goal event
      newEvents[e.index] = { ...newEvents[e.index], minute: min, player_id: e.player_id, is_opponent: e.is_opponent };
      if (e.is_opponent) {
        delete newEvents[e.index].player_id;
        newEvents[e.index].is_opponent = true;
      }
      // Find and update/add/remove paired assist
      const existingAssistIdx = newEvents.findIndex((ev, i) => i !== e.index && ev.event_type === 'assist' && ev.minute === events[e.index].minute);
      if (e.assist_player_id && !e.is_opponent) {
        if (existingAssistIdx >= 0) {
          newEvents[existingAssistIdx] = { ...newEvents[existingAssistIdx], player_id: e.assist_player_id, minute: min };
        } else {
          newEvents.push({ event_type: 'assist', player_id: e.assist_player_id, minute: min });
        }
      } else if (existingAssistIdx >= 0) {
        newEvents.splice(existingAssistIdx, 1);
      }
    } else if (e.event_type === 'yellow_card' || e.event_type === 'red_card') {
      newEvents[e.index] = { ...newEvents[e.index], minute: min, player_id: e.player_id, is_opponent: e.is_opponent };
      if (e.is_opponent) delete newEvents[e.index].player_id;
    } else if (e.event_type === 'substitution') {
      // Update sub_in
      newEvents[e.index] = { ...newEvents[e.index], player_id: e.sub_on_id, minute: min };
      // Find and update sub_out
      const subOutIdx = newEvents.findIndex((ev, i) => i !== e.index && ev.event_type === 'substitution_out' && ev.minute === events[e.index].minute);
      if (subOutIdx >= 0) {
        newEvents[subOutIdx] = { ...newEvents[subOutIdx], player_id: e.sub_off_id, minute: min };
      }
    }

    setEvents(newEvents.sort((a, b) => a.minute - b.minute));
    setHasUnsavedChanges(true);
    setEditingEvent(null);
    toast.success('Event updated');
  };

  // Swap two player positions on the pitch
  const swapPositions = useCallback((indexA, indexB) => {
    if (indexA === indexB || !match?.lineup) return;
    const newLineup = [...match.lineup];
    const newNumbers = { ...match.lineup_numbers };
    // Swap player IDs
    [newLineup[indexA], newLineup[indexB]] = [newLineup[indexB], newLineup[indexA]];
    // Swap their assigned numbers
    [newNumbers[indexA], newNumbers[indexB]] = [newNumbers[indexB], newNumbers[indexA]];
    setMatch(prev => ({ ...prev, lineup: newLineup, lineup_numbers: newNumbers }));
    setHasUnsavedChanges(true);
    setSwapSource(null);
  }, [match]);

  // Handle tap on a player marker for swapping
  const handleMarkerTap = useCallback((index, playerId) => {
    if (activeEvent) return;
    if (!playerId) return;
    if (swapSource !== null && swapSource !== index) {
      swapPositions(swapSource, index);
    } else if (swapSource === index) {
      setSwapSource(null);
    } else {
      setSwapSource(index);
    }
  }, [activeEvent, swapSource, swapPositions]);

  // Mark this match as active in localStorage so dashboard can offer "Return" option
  useEffect(() => {
    localStorage.setItem('matchday_active_match_id', matchId);
    return () => {}; // Don't clear on unmount — only clear on END MATCH
  }, [matchId]);

  useEffect(() => { fetchMatch(); }, [matchId]);

  const fetchMatch = async () => {
    try {
      const [matchRes, playersRes] = await Promise.all([
        axios.get(`${API}/matches/${matchId}`),
        axios.get(`${API}/players`)
      ]);
      const m = matchRes.data;
      setMatch(m);
      setPlayers(playersRes.data);
      setEvents(m.events || []);
      setOurScore(m.our_score || 0);
      setOppScore(m.opponent_score || 0);

      // Fetch opponent badge if user has badges enabled
      if (user?.show_opponent_badges !== false && m.opponent) {
        try {
          const oppRes = await axios.get(`${API}/opponents`);
          const target = m.opponent.toLowerCase().trim();
          const match_opp = (oppRes.data || []).find(o => o?.name?.toLowerCase().trim() === target);
          setOpponentBadge(resolveBadgeUrl(match_opp?.badge));
        } catch (_) {
          // silent
        }
      }
    } catch (error) {
      toast.error('Failed to load match');
      navigate('/matches');
    } finally {
      setLoading(false);
    }
  };

  const getPlayer = useCallback((id) => players.find(p => p.id === id), [players]);

  const getPlayerDisplay = (id) => {
    const player = getPlayer(id);
    if (!player) return { name: '?', number: '?' };
    const posIndex = match?.lineup?.indexOf(id);
    const number = posIndex >= 0 ? (match?.lineup_numbers?.[posIndex] || player.jersey_number || '?') : (match?.sub_numbers?.[match?.subs?.findIndex(s => s?.playerId === id)] || player.jersey_number || '?');
    return { name: player.name, number };
  };

  // Active lineup = starting lineup adjusted for substitutions
  const getActiveLineup = useCallback(() => {
    if (!match?.lineup) return [];
    const current = [...match.lineup];
    events.filter(e => e.event_type === 'substitution_in').forEach(subEvent => {
      const outEvent = events.find(e => e.event_type === 'substitution_out' && e.minute === subEvent.minute);
      if (outEvent) {
        const idx = current.indexOf(outEvent.player_id);
        if (idx >= 0) current[idx] = subEvent.player_id;
      }
    });
    return current;
  }, [match, events]);

  // Available bench = subs minus those already subbed on
  const getAvailableBench = useCallback(() => {
    if (!match?.subs) return [];
    const subbedOnIds = events.filter(e => e.event_type === 'substitution_in').map(e => e.player_id);
    return match.subs
      .filter(s => s && s.playerId && !subbedOnIds.includes(s.playerId))
      .map(s => s.playerId);
  }, [match, events]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/matches/${matchId}`, {
        ...match,
        events,
        our_score: ourScore,
        opponent_score: oppScore,
        penalty_shootout: penalties.length > 0 ? penalties : undefined,
        our_score_pens: penalties.length > 0 ? getShootoutScore().home : undefined,
        opponent_score_pens: penalties.length > 0 ? getShootoutScore().away : undefined,
        status: 'result'
      });
      toast.success('Match saved');
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEndMatch = async (potmPlayerId) => {
    setSaving(true);
    try {
      // Build player_stats with POTM flag
      const allPlayerIds = [
        ...(match?.lineup || []),
        ...(match?.subs || []).map(s => s?.playerId)
      ].filter(Boolean);

      const isFriendly = match?.match_type === 'friendly';

      // Build player_stats (skip for friendly matches)
      const playerStats = isFriendly ? [] : allPlayerIds.map(pid => {
        const isStarter = (match?.lineup || []).includes(pid);
        const subbedOn = events.some(e => e.event_type === 'substitution_in' && e.player_id === pid);
        const subbedOff = events.some(e => e.event_type === 'substitution_out' && e.player_id === pid);
        return {
          player_id: pid,
          goals: events.filter(e => e.event_type === 'goal' && e.player_id === pid && !e.is_opponent).length,
          goals_penalty: events.filter(e => e.event_type === 'goal' && e.player_id === pid && !e.is_opponent && e.goal_type === 'penalty').length,
          goals_inside_box: events.filter(e => e.event_type === 'goal' && e.player_id === pid && !e.is_opponent && e.goal_type === 'shot_inside').length,
          goals_outside_box: events.filter(e => e.event_type === 'goal' && e.player_id === pid && !e.is_opponent && e.goal_type === 'shot_outside').length,
          goals_header: events.filter(e => e.event_type === 'goal' && e.player_id === pid && !e.is_opponent && e.goal_type === 'header').length,
          own_goals: events.filter(e => (e.event_type === 'own_goal' && e.player_id === pid) || (e.event_type === 'goal' && e.player_id === pid && e.goal_type === 'own_goal')).length,
          penalties_conceded: events.filter(e => e.event_type === 'penalty_conceded' && e.player_id === pid).length,
          assists: events.filter(e => e.event_type === 'assist' && e.player_id === pid).length,
          yellow_cards: events.filter(e => e.event_type === 'yellow_card' && e.player_id === pid && !e.is_opponent).length,
          red_cards: events.filter(e => e.event_type === 'red_card' && e.player_id === pid && !e.is_opponent).length,
          is_starter: isStarter,
          is_sub: subbedOn,
          is_unused_sub: !isStarter && !subbedOn,
          is_pom: pid === potmPlayerId,
          minutes_played: isStarter ? (subbedOff ? (events.find(e => e.event_type === 'substitution_out' && e.player_id === pid)?.minute || 90) : 90) : (subbedOn ? (90 - (events.find(e => e.event_type === 'substitution_in' && e.player_id === pid)?.minute || 90)) : 0),
        };
      });

      await axios.put(`${API}/matches/${matchId}`, {
        ...match,
        events,
        our_score: ourScore,
        opponent_score: oppScore,
        player_stats: playerStats,
        penalty_shootout: penalties.length > 0 ? penalties : undefined,
        our_score_pens: penalties.length > 0 ? getShootoutScore().home : undefined,
        opponent_score_pens: penalties.length > 0 ? getShootoutScore().away : undefined,
        status: 'result'
      });
      localStorage.removeItem('matchday_active_match_id');
      localStorage.removeItem(`matchday_timer_${matchId}`);
      toast.success('Match ended and saved');
      navigate('/matches');
    } catch (error) {
      toast.error('Failed to save match');
    } finally {
      setSaving(false);
      setShowEndDialog(false);
      setShowPotmSelect(false);
    }
  };

  const confirmEvent = () => {
    if (!eventMinute) {
      toast.error('Enter the minute');
      return;
    }
    const min = parseInt(eventMinute);
    const newEvents = [];

    if (activeEvent === 'goal') {
      if (isOpponentEvent && isOwnGoal) {
        if (!selectedPlayer) { toast.error('Select the player who scored the own goal'); return; }
        newEvents.push({ event_type: 'own_goal', player_id: selectedPlayer, minute: min });
        setOppScore(prev => prev + 1);
      } else if (isOpponentEvent && isPenalty) {
        newEvents.push({ event_type: 'goal', is_opponent: true, is_penalty: true, minute: min });
        if (penaltyConcededBy) {
          newEvents.push({ event_type: 'penalty_conceded', player_id: penaltyConcededBy, minute: min });
        }
        setOppScore(prev => prev + 1);
      } else if (isOpponentEvent) {
        newEvents.push({ event_type: 'goal', is_opponent: true, minute: min });
        setOppScore(prev => prev + 1);
      } else {
        if (!selectedPlayer) { toast.error('Select a player'); return; }
        const goalEvent = { event_type: 'goal', player_id: selectedPlayer, minute: min };
        if (goalType) goalEvent.goal_type = goalType;
        newEvents.push(goalEvent);
        // Add assist if selected
        if (assistPlayer && assistPlayer !== 'none') {
          newEvents.push({ event_type: 'assist', player_id: assistPlayer, minute: min });
        }
        setOurScore(prev => prev + 1);
      }
    } else if (activeEvent === 'yellow_card' || activeEvent === 'red_card') {
      if (isOpponentEvent) {
        newEvents.push({ event_type: activeEvent, is_opponent: true, minute: min });
      } else {
        if (!selectedPlayer) { toast.error('Select a player'); return; }
        newEvents.push({ event_type: activeEvent, player_id: selectedPlayer, minute: min });
      }
    } else if (activeEvent === 'substitution') {
      if (!subOffPlayer || !selectedPlayer) { toast.error('Select both players'); return; }
      newEvents.push({ event_type: 'substitution_out', player_id: subOffPlayer, minute: min });
      newEvents.push({ event_type: 'substitution_in', player_id: selectedPlayer, minute: min });
    }

    setEvents(prev => [...prev, ...newEvents].sort((a, b) => a.minute - b.minute));
    setHasUnsavedChanges(true);
    resetEventState();
    toast.success('Event added');
  };

  const removeEvent = (index) => {
    const event = events[index];
    // If removing a sub_in, also remove the paired sub_out
    if (event.event_type === 'substitution_in') {
      const outIdx = events.findIndex((e, i) => i !== index && e.event_type === 'substitution_out' && e.minute === event.minute);
      const filtered = events.filter((_, i) => i !== index && i !== outIdx);
      setEvents(filtered);
    } else if (event.event_type === 'substitution_out') {
      const inIdx = events.findIndex((e, i) => i !== index && e.event_type === 'substitution_in' && e.minute === event.minute);
      const filtered = events.filter((_, i) => i !== index && i !== inIdx);
      setEvents(filtered);
    } else {
      // If removing a goal, adjust score
      if (event.event_type === 'goal') {
        if (event.is_opponent) setOppScore(prev => Math.max(0, prev - 1));
        else setOurScore(prev => Math.max(0, prev - 1));
      }
      if (event.event_type === 'own_goal') {
        setOppScore(prev => Math.max(0, prev - 1));
      }
      // Also remove paired assist if removing a goal
      if (event.event_type === 'goal' && !event.is_opponent) {
        const assistIdx = events.findIndex((e, i) => i !== index && e.event_type === 'assist' && e.minute === event.minute);
        if (assistIdx >= 0) {
          setEvents(events.filter((_, i) => i !== index && i !== assistIdx));
          setHasUnsavedChanges(true);
          toast.success('Event removed');
          return;
        }
      }
      // Also remove paired penalty_conceded if removing an opponent penalty goal
      if (event.event_type === 'goal' && event.is_opponent && event.is_penalty) {
        const penIdx = events.findIndex((e, i) => i !== index && e.event_type === 'penalty_conceded' && e.minute === event.minute);
        if (penIdx >= 0) {
          setEvents(events.filter((_, i) => i !== index && i !== penIdx));
          setHasUnsavedChanges(true);
          toast.success('Event removed');
          return;
        }
      }
      setEvents(events.filter((_, i) => i !== index));
    }
    setHasUnsavedChanges(true);
    toast.success('Event removed');
  };

  // Penalty shootout helpers
  const getShootoutScore = useCallback(() => {
    const home = penalties.filter(p => p.team === 'home' && p.result === 'scored').length;
    const away = penalties.filter(p => p.team === 'away' && p.result === 'scored').length;
    return { home, away };
  }, [penalties]);

  const getShootoutRound = useCallback(() => {
    const homeCount = penalties.filter(p => p.team === 'home').length;
    const awayCount = penalties.filter(p => p.team === 'away').length;
    return Math.max(homeCount, awayCount);
  }, [penalties]);

  const isShootoutDecided = useCallback(() => {
    const homeTaken = penalties.filter(p => p.team === 'home').length;
    const awayTaken = penalties.filter(p => p.team === 'away').length;
    const homeScored = penalties.filter(p => p.team === 'home' && p.result === 'scored').length;
    const awayScored = penalties.filter(p => p.team === 'away' && p.result === 'scored').length;

    // Need at least some penalties taken
    if (homeTaken === 0 && awayTaken === 0) return false;

    // Regulation: 5 penalties each
    const inRegulation = homeTaken <= 5 && awayTaken <= 5;

    if (inRegulation) {
      const homeRemaining = 5 - homeTaken;
      const awayRemaining = 5 - awayTaken;
      // Home can't catch up: away leads by more than home has left
      if (awayScored > homeScored + homeRemaining) return true;
      // Away can't catch up: home leads by more than away has left
      if (homeScored > awayScored + awayRemaining) return true;
      // After all 5 taken each, if tied go to sudden death, if not → decided
      if (homeTaken === 5 && awayTaken === 5 && homeScored !== awayScored) return true;
      return false;
    }

    // Sudden death: after 5 each, both must have equal attempts
    // If equal attempts and scores differ → decided
    if (homeTaken === awayTaken && homeScored !== awayScored) return true;

    return false;
  }, [penalties]);

  const nextPenTeam = useCallback(() => {
    const homeCount = penalties.filter(p => p.team === 'home').length;
    const awayCount = penalties.filter(p => p.team === 'away').length;
    return homeCount <= awayCount ? 'home' : 'away';
  }, [penalties]);

  const addPenalty = useCallback((team, playerId, result) => {
    setPenalties(prev => [...prev, { team, player_id: playerId, result }]);
    setPenShootoutPlayer(null);
    setHasUnsavedChanges(true);
  }, []);

  const resetEventState = () => {
    setActiveEvent(null);
    setSelectedPlayer(null);
    setAssistPlayer(null);
    setSubOffPlayer(null);
    setEventMinute('');
    setIsOpponentEvent(false);
    setIsOwnGoal(false);
    setIsPenalty(false);
    setPenaltyConcededBy(null);
    setGoalType(null);
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'goal': case 'penalty_goal': return '⚽';
      case 'own_goal': return '⚽ (OG)';
      case 'yellow_card': return '🟨';
      case 'red_card': return '🟥';
      case 'substitution_in': return '🔼';
      case 'substitution_out': return '🔽';
      case 'assist': return '🅰️';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
      </div>
    );
  }

  if (!match) return null;

  const activeLineup = getActiveLineup();
  const availableBench = getAvailableBench();
  const positions = FORMATION_POSITIONS[match.formation] || FORMATION_POSITIONS['4-4-2'];

  // ── New scoreboard header helpers ────────────────────────────────────
  // Venue chip text (HOME / AWAY / NEUTRAL) — defaults to HOME if missing
  const venueLabel = (match.venue || 'home').toUpperCase();

  // Period chip — { text, tone } drives the colour
  const getPeriodChip = () => {
    const mm = String(timerMinutes).padStart(2, '0');
    const ss = String(timerSeconds).padStart(2, '0');
    const time = `${mm}:${ss}`;
    switch (matchPeriod) {
      case 'pre': {
        const ko = match.kick_off_time || '14:00';
        return { text: `KO · ${ko}`, tone: 'amber' };
      }
      case 'first_half':   return { text: `1st Half · ${time}`, tone: 'emerald' };
      case 'half_time':    return { text: 'HT', tone: 'yellow' };
      case 'second_half':  return { text: `2nd Half · ${time}`, tone: 'emerald' };
      case 'et_first':     return { text: `ET 1st · ${time}`, tone: 'emerald' };
      case 'et_half_time': return { text: 'HT (ET)', tone: 'yellow' };
      case 'et_second':    return { text: `ET 2nd · ${time}`, tone: 'emerald' };
      case 'penalty':      return { text: 'PEN', tone: 'red' };
      case 'full_time':    return { text: 'FT', tone: 'gray' };
      default:             return { text: time, tone: 'emerald' };
    }
  };
  const periodChip = getPeriodChip();
  const PERIOD_CHIP_CLASSES = {
    amber:   'border-amber-500/60 text-amber-300',
    emerald: 'border-emerald-500/50 text-emerald-300',
    yellow:  'border-yellow-500/60 text-yellow-300',
    red:     'border-red-500/60 text-red-300',
    gray:    'border-gray-500/60 text-gray-300',
  };

  // Club + opponent badges (respect user setting)
  const showBadges = user?.show_opponent_badges !== false;
  const clubBadge  = resolveBadgeUrl(user?.club_badge);
  const ourTeamName = user?.team_name || 'Home';

  // Match-type chip (uppercased value e.g. LEAGUE / CUP / FRIENDLY / TOURNAMENT)
  const matchTypeChip = (match.match_type || '').replace(/_/g, ' ').toUpperCase();

  // Open the existing event flows from the new action row
  const openEvent = (eventId) => {
    if (activeEvent === eventId) { resetEventState(); return; }
    resetEventState();
    setActiveEvent(eventId);
    if (timerRunning || timerMinutes > 0) {
      setEventMinute(String(timerMinutes));
    }
  };
  const isCardActive = activeEvent === 'yellow_card' || activeEvent === 'red_card';

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="match-day-mode">
      {/* ── Match Day Live Header (locked design 2026-05-23) ───────── */}
      <div className="sticky top-0 z-30 bg-gradient-to-br from-gray-950 via-gray-900 to-emerald-950 border-b border-gray-800 shadow-lg">
        {/* Top bar: back · LIVE + venue · save */}
        <div className="flex items-center px-3 py-2.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (hasUnsavedChanges) { setShowUnsavedDialog(true); } else { navigate(-1); }
            }}
            className="text-gray-300 hover:text-white shrink-0"
            data-testid="matchday-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 flex items-center justify-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-600 text-white text-xs font-bold uppercase tracking-[0.2em]"
              data-testid="matchday-live-badge"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Live
            </span>
            <span
              className="text-sm font-bold uppercase tracking-[0.2em] text-white"
              data-testid="matchday-venue-label"
            >
              {venueLabel}
            </span>
          </div>
          <button
            onClick={(e) => { e.currentTarget.blur(); handleSave(); }}
            disabled={saving}
            className="px-4 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold disabled:opacity-50 shrink-0"
            data-testid="matchday-save-btn"
          >
            {saving ? '...' : 'Save'}
          </button>
        </div>

        {/* Scoreline + period chip + fixture subtitle + match-type chip */}
        <div className="px-4 pb-3 pt-1 text-center" data-testid="matchday-score">
          <div className="flex items-center justify-center gap-4 sm:gap-6">
            {showBadges && (
              clubBadge ? (
                <img src={clubBadge} alt={`${ourTeamName} badge`} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-contain shrink-0 bg-white/5 p-1" />
              ) : (
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center shrink-0">
                  <Shield className="w-7 h-7 text-white/30" strokeWidth={1.5} />
                </div>
              )
            )}
            <div className="flex items-center gap-4 sm:gap-6 font-mono">
              <span className="text-5xl sm:text-6xl font-bold text-white leading-none tabular-nums" data-testid="our-score">{ourScore}</span>
              <span className="text-3xl sm:text-4xl text-gray-500 leading-none">—</span>
              <span className="text-5xl sm:text-6xl font-bold text-white leading-none tabular-nums" data-testid="opp-score">{oppScore}</span>
            </div>
            {showBadges && (
              opponentBadge ? (
                <img src={opponentBadge} alt={`${match.opponent} badge`} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-contain shrink-0 bg-white/5 p-1" />
              ) : (
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center shrink-0">
                  <Shield className="w-7 h-7 text-white/30" strokeWidth={1.5} />
                </div>
              )
            )}
          </div>
          <button
            onClick={() => {
              if (matchPeriod === 'penalty') { setShowShootout(true); return; }
              if (timerRunning) { setShowTimerMenu(true); }
              else { setShowTimerConfirm(true); }
            }}
            className={`mt-3 inline-flex items-center px-4 py-1 rounded-full border-2 text-xs font-bold uppercase tracking-[0.18em] transition-all ${PERIOD_CHIP_CLASSES[periodChip.tone]}`}
            data-testid="matchday-period-chip"
          >
            {periodChip.text}
          </button>
          <p className="text-sm text-gray-300 mt-2 truncate" data-testid="matchday-fixture">
            {ourTeamName} vs {match.opponent}
          </p>
          {matchTypeChip && (
            <span
              className="mt-1 inline-block px-2 py-0.5 rounded bg-gray-900 text-white text-[10px] font-bold uppercase tracking-[0.2em]"
              data-testid="matchday-type-chip"
            >
              {matchTypeChip}
            </span>
          )}
        </div>

        {/* 5-button action row: GOAL · CARD · SUB · VAR · END */}
        <div className="px-3 pb-3 grid grid-cols-5 gap-1.5" data-testid="quick-event-buttons">
          <button
            onClick={() => { tapHaptic(); openEvent('goal'); }}
            className={`flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 py-2.5 sm:py-3 rounded-xl font-bold uppercase tracking-wider text-[10px] sm:text-xs transition-all ${
              activeEvent === 'goal' ? 'bg-emerald-500 text-white ring-2 ring-white/30 scale-[1.02]' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
            data-testid="quick-event-goal"
          >
            <CircleDot className="w-4 h-4 sm:w-4 sm:h-4" strokeWidth={2.5} />
            <span>Goal</span>
          </button>
          <button
            onClick={() => { tapHaptic(); openEvent(activeEvent === 'red_card' ? 'red_card' : 'yellow_card'); }}
            className={`flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 py-2.5 sm:py-3 rounded-xl font-bold uppercase tracking-wider text-[10px] sm:text-xs transition-all ${
              isCardActive ? 'bg-yellow-400 text-yellow-950 ring-2 ring-white/30 scale-[1.02]' : 'bg-yellow-500 hover:bg-yellow-400 text-yellow-950'
            }`}
            data-testid="quick-event-card"
          >
            <Square className="w-4 h-4" strokeWidth={2.5} />
            <span>Card</span>
          </button>
          <button
            onClick={() => { tapHaptic(); openEvent('substitution'); }}
            className={`flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 py-2.5 sm:py-3 rounded-xl font-bold uppercase tracking-wider text-[10px] sm:text-xs transition-all ${
              activeEvent === 'substitution' ? 'bg-blue-500 text-white ring-2 ring-white/30 scale-[1.02]' : 'bg-blue-600 hover:bg-blue-500 text-white'
            }`}
            data-testid="quick-event-substitution"
          >
            <ArrowLeftRight className="w-4 h-4" strokeWidth={2.5} />
            <span>Sub</span>
          </button>
          <button
            onClick={() => {
              tapHaptic();
              resetEventState();
              setShowTimeline(true);
              // Scroll the timeline section into view after the next paint
              setTimeout(() => {
                document.getElementById('event-timeline-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 80);
            }}
            className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 py-2.5 sm:py-3 rounded-xl font-bold uppercase tracking-wider text-[10px] sm:text-xs bg-purple-600 hover:bg-purple-500 text-white transition-all"
            data-testid="quick-event-var"
            title="VAR · review and edit any prior match event"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={2.5} />
            <span>VAR</span>
          </button>
          <button
            onClick={() => { tapHaptic(); setShowEndDialog(true); }}
            className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 py-2.5 sm:py-3 rounded-xl font-bold uppercase tracking-wider text-[10px] sm:text-xs bg-red-600 hover:bg-red-500 text-white transition-all"
            data-testid="quick-event-end"
          >
            <StopCircle className="w-4 h-4" strokeWidth={2.5} />
            <span>End</span>
          </button>
        </div>
      </div>

      {/* Voice Listening Indicator */}
      {isListening && (
        <div className="px-3 pt-3" data-testid="voice-listening-indicator">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center gap-3">
            <div className="relative">
              <Mic className="w-5 h-5 text-red-400" />
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-400">Listening...</p>
              <p className="text-xs text-gray-500">Speak your match event clearly</p>
            </div>
            <Button variant="ghost" size="sm" onClick={stopListening} className="text-red-400 hover:text-red-300 text-xs" data-testid="voice-stop-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Voice Parsing Indicator */}
      {voiceParsing && (
        <div className="px-3 pt-3" data-testid="voice-parsing-indicator">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
            <div>
              <p className="text-sm font-semibold text-blue-400">Processing voice...</p>
              <p className="text-xs text-gray-500 truncate">"{voiceTranscript}"</p>
            </div>
          </div>
        </div>
      )}

      {/* Voice Event Confirmation Card */}
      {voiceResult && !voiceParsing && (
        <div className="px-3 pt-3" data-testid="voice-confirmation-card">
          <div className="bg-gray-900 border border-emerald-500/40 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-emerald-400" />
              <p className="text-xs text-gray-500 flex-1 truncate">"{voiceResult.raw_transcript}"</p>
              {voiceResult.confidence && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${
                  voiceResult.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                  voiceResult.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`} data-testid="voice-confidence">{voiceResult.confidence}</span>
              )}
            </div>

            {/* Parsed event display */}
            <div className="flex items-center gap-3 bg-gray-800 rounded-lg p-3">
              <span className="text-xl">
                {voiceResult.event_type === 'goal' ? '⚽' :
                 voiceResult.event_type === 'yellow_card' ? '🟨' :
                 voiceResult.event_type === 'red_card' ? '🟥' :
                 voiceResult.event_type === 'substitution' ? '🔄' :
                 voiceResult.event_type === 'position_swap' ? '↔️' : '📋'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white capitalize">
                  {voiceResult.event_type?.replace('_', ' ')}
                  {voiceResult.minute ? ` — ${voiceResult.minute}'` : ''}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {voiceResult.is_opponent ? 'Opponent' :
                   voiceResult.event_type === 'substitution'
                     ? `Off: #${voiceResult.sub_off_number || '?'} ${voiceResult.sub_off_name || ''} → On: #${voiceResult.sub_on_number || '?'} ${voiceResult.sub_on_name || ''}`
                     : voiceResult.event_type === 'position_swap'
                       ? `#${voiceResult.swap_player1_number || '?'} ${voiceResult.swap_player1_name || ''} ↔ #${voiceResult.swap_player2_number || '?'} ${voiceResult.swap_player2_name || ''}`
                       : `#${voiceResult.player_number || '?'} ${voiceResult.player_name || 'Unknown'}`
                  }
                  {voiceResult.event_type === 'goal' && voiceResult.assist_player_id && (
                    ` (assist: #${voiceResult.assist_player_number || '?'} ${voiceResult.assist_player_name || ''})`
                  )}
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button
                onClick={confirmVoiceEvent}
                className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold gap-1.5"
                data-testid="voice-confirm-btn"
              >
                <Check className="w-4 h-4" /> Confirm
              </Button>
              <Button
                variant="ghost"
                onClick={dismissVoiceResult}
                className="rounded-xl text-gray-400 hover:text-white text-sm"
                data-testid="voice-dismiss-btn"
              >
                <XIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Simplified Pitch View */}
      <div className="px-3 pt-3">
        <div
          ref={pitchRef}
          className="relative rounded-xl overflow-hidden mx-auto"
          style={{
            aspectRatio: '2/3',
            maxWidth: '100%',
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_9fdf8e2b-a210-4b58-8435-eeb33315a6e2/artifacts/kkyzv9x6_Untitled%20design%20%2872%29.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          data-testid="matchday-pitch"
        >
          {positions.map((pos, index) => {
            const playerId = activeLineup[index];
            const player = playerId ? getPlayer(playerId) : null;
            const display = playerId ? getPlayerDisplay(playerId) : null;
            const number = display?.number || '';
            const isSelected = selectedPlayer === playerId || subOffPlayer === playerId;
            const wasSubbedOn = playerId && events.some(e => e.event_type === 'substitution_in' && e.player_id === playerId);
            const isSwapSource = swapSource === index && !activeEvent;

            return (
              <div
                key={pos.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  transition: 'left 0.2s ease, top 0.2s ease',
                }}
                onClick={() => {
                  if (activeEvent && player) {
                    if (activeEvent === 'substitution') {
                      setSubOffPlayer(playerId);
                    } else {
                      setSelectedPlayer(playerId);
                    }
                  } else {
                    handleMarkerTap(index, playerId);
                  }
                }}
              >
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs font-bold shadow-lg transition-all ${
                  player
                    ? isSelected
                      ? 'bg-amber-400 text-gray-900 ring-2 ring-amber-300 scale-110'
                      : isSwapSource
                        ? 'bg-amber-400 text-gray-900 ring-2 ring-amber-300 scale-110'
                        : wasSubbedOn
                          ? 'bg-blue-500 text-white ring-2 ring-blue-300'
                          : 'bg-white text-gray-900'
                    : 'bg-white/20 text-white/50 border border-dashed border-white/30'
                }`}>
                  {number}
                </div>
                {player && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0.5 whitespace-nowrap">
                    <span className={`text-[10px] font-medium text-white px-1 py-0.5 rounded ${isSwapSource ? 'bg-amber-500/80' : 'bg-black/60'}`}>
                      {player.name?.split(' ').pop()}
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Swap mode hint */}
          {swapSource !== null && !activeEvent && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30">
              <span className="text-[10px] font-semibold text-amber-300 bg-black/70 px-2 py-1 rounded-full">
                Tap another player to swap
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Event input panel — opens when an action button in the header is active */}
      <div className="px-3" data-testid="event-input-wrapper">
      {/* Yellow/Red card type toggle — shown when a card event is active */}
      {isCardActive && (
        <div className="pt-3" data-testid="card-type-toggle">
          <div className="flex rounded-lg bg-gray-800 p-0.5">
            <button
              onClick={() => setActiveEvent('yellow_card')}
              className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${activeEvent === 'yellow_card' ? 'bg-yellow-400 text-yellow-950' : 'text-gray-400'}`}
              data-testid="card-type-yellow"
            >
              Yellow
            </button>
            <button
              onClick={() => setActiveEvent('red_card')}
              className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${activeEvent === 'red_card' ? 'bg-red-600 text-white' : 'text-gray-400'}`}
              data-testid="card-type-red"
            >
              Red
            </button>
          </div>
        </div>
      )}

      {activeEvent && (
        <div className="pt-3 space-y-3 animate-in slide-in-from-bottom-2" data-testid="event-input-panel">
          <div className="bg-gray-900 rounded-xl p-3 border border-gray-800">
            {/* Team Toggle for goals/cards */}
            {activeEvent !== 'substitution' && (
              <div className="flex rounded-lg bg-gray-800 p-0.5 mb-3">
                <button
                  onClick={() => { setIsOpponentEvent(false); setSelectedPlayer(null); }}
                  className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${!isOpponentEvent ? 'bg-emerald-600 text-white' : 'text-gray-400'}`}
                  data-testid="event-our-team"
                >
                  {user?.team_name || 'Our Team'}
                </button>
                <button
                  onClick={() => { setIsOpponentEvent(true); setSelectedPlayer(null); }}
                  className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${isOpponentEvent ? 'bg-red-600 text-white' : 'text-gray-400'}`}
                  data-testid="event-opponent"
                >
                  {match.opponent}
                </button>
              </div>
            )}

            {/* Own Goal toggle (opponent goal only) */}
            {activeEvent === 'goal' && isOpponentEvent && (
              <div className="mb-3">
                <button
                  onClick={() => { setIsOwnGoal(prev => !prev); setSelectedPlayer(null); setIsPenalty(false); setPenaltyConcededBy(null); }}
                  className={`w-full py-2 rounded-lg text-xs font-bold transition-all ${
                    isOwnGoal ? 'bg-red-600 text-white ring-1 ring-red-400' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                  data-testid="event-own-goal-toggle"
                >
                  OG (Own Goal)
                </button>
              </div>
            )}

            {/* OG Player Selection */}
            {activeEvent === 'goal' && isOpponentEvent && isOwnGoal && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Who scored the own goal?</p>
                <div className="flex flex-wrap gap-1.5 ">
                  {getActiveLineup().filter(Boolean).map(pid => {
                    const p = getPlayerDisplay(pid);
                    return (
                      <button key={pid} onClick={() => setSelectedPlayer(pid)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          selectedPlayer === pid ? 'bg-red-600 text-white ring-1 ring-red-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`} data-testid={`og-player-${pid}`}>
                        #{p.number} {p.name?.split(' ').pop()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Penalty toggle (opponent goal only, not OG) */}
            {activeEvent === 'goal' && isOpponentEvent && !isOwnGoal && (
              <div className="mb-3">
                <button
                  onClick={() => { setIsPenalty(prev => !prev); setPenaltyConcededBy(null); }}
                  className={`w-full py-2 rounded-lg text-xs font-bold transition-all ${
                    isPenalty ? 'bg-red-600 text-white ring-1 ring-red-400' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                  data-testid="event-penalty-toggle"
                >
                  Penalty
                </button>
              </div>
            )}

            {/* Penalty Conceded By selection */}
            {activeEvent === 'goal' && isOpponentEvent && isPenalty && !isOwnGoal && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Who conceded the penalty? <span className="normal-case font-normal">(optional)</span></p>
                <div className="flex flex-wrap gap-1.5 ">
                  {getActiveLineup().filter(Boolean).map(pid => {
                    const p = getPlayerDisplay(pid);
                    return (
                      <button key={pid} onClick={() => setPenaltyConcededBy(penaltyConcededBy === pid ? null : pid)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          penaltyConcededBy === pid ? 'bg-red-600 text-white ring-1 ring-red-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`} data-testid={`penalty-conceded-${pid}`}>
                        #{p.number} {p.name?.split(' ').pop()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Substitution: Player Going Off */}
            {activeEvent === 'substitution' && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Player Off (tap on pitch or select):</p>
                <div className="flex flex-wrap gap-1.5 ">
                  {activeLineup.filter(Boolean).map(pid => {
                    const p = getPlayerDisplay(pid);
                    return (
                      <button key={pid} onClick={() => setSubOffPlayer(pid)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          subOffPlayer === pid ? 'bg-red-600 text-white ring-1 ring-red-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`} data-testid={`sub-off-${pid}`}>
                        #{p.number} {p.name?.split(' ').pop()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Player Selection (our team, non-sub events) */}
            {!isOpponentEvent && activeEvent !== 'substitution' && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Select Player (tap on pitch or select):</p>
                <div className="flex flex-wrap gap-1.5 ">
                  {activeLineup.filter(Boolean).map(pid => {
                    const p = getPlayerDisplay(pid);
                    return (
                      <button key={pid} onClick={() => { setSelectedPlayer(pid); setAssistPlayer(null); }}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          selectedPlayer === pid ? 'bg-emerald-600 text-white ring-1 ring-emerald-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`} data-testid={`select-player-${pid}`}>
                        #{p.number} {p.name?.split(' ').pop()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Assist Selection (shown after goal scorer selected, our team only) */}
            {activeEvent === 'goal' && !isOpponentEvent && selectedPlayer && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Assist <span className="normal-case font-normal">(optional)</span>:</p>
                <div className="flex flex-wrap gap-1.5 ">
                  <button
                    onClick={() => setAssistPlayer('none')}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      assistPlayer === 'none' ? 'bg-gray-600 text-white ring-1 ring-gray-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid="assist-none"
                  >
                    No Assist
                  </button>
                  {activeLineup.filter(pid => pid && pid !== selectedPlayer).map(pid => {
                    const p = getPlayerDisplay(pid);
                    return (
                      <button key={pid} onClick={() => setAssistPlayer(pid)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          assistPlayer === pid ? 'bg-blue-600 text-white ring-1 ring-blue-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`} data-testid={`assist-player-${pid}`}>
                        #{p.number} {p.name?.split(' ').pop()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Goal Type Selection (our team goals, after scorer selected) */}
            {activeEvent === 'goal' && !isOpponentEvent && selectedPlayer && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Goal Type <span className="normal-case font-normal">(optional)</span>:</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: 'penalty', label: 'Penalty' },
                    { id: 'shot_inside', label: 'Shot (Inside Box)' },
                    { id: 'shot_outside', label: 'Shot (Outside Box)' },
                    { id: 'header', label: 'Header' },
                    { id: 'own_goal', label: 'OG' },
                  ].map(gt => (
                    <button
                      key={gt.id}
                      onClick={() => setGoalType(goalType === gt.id ? null : gt.id)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        goalType === gt.id ? 'bg-emerald-600 text-white ring-1 ring-emerald-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                      data-testid={`goal-type-${gt.id}`}
                    >
                      {gt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Substitution: Player Coming On */}
            {activeEvent === 'substitution' && subOffPlayer && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Player On:</p>
                <div className="flex flex-wrap gap-1.5 ">
                  {availableBench.map(pid => {
                    const p = getPlayerDisplay(pid);
                    return (
                      <button key={pid} onClick={() => setSelectedPlayer(pid)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          selectedPlayer === pid ? 'bg-blue-600 text-white ring-1 ring-blue-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`} data-testid={`sub-on-${pid}`}>
                        #{p.number} {p.name?.split(' ').pop()}
                      </button>
                    );
                  })}
                  {availableBench.length === 0 && (
                    <p className="text-xs text-gray-500 py-2">No subs available</p>
                  )}
                </div>
              </div>
            )}

            {/* Minute + Confirm */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 flex-1">
                <Timer className="w-4 h-4 text-gray-500" />
                <input
                  ref={minuteRef}
                  type="number"
                  min="1"
                  max="120"
                  value={eventMinute}
                  onChange={(e) => setEventMinute(e.target.value)}
                  placeholder="Min"
                  className="w-16 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-sm text-white text-center focus:ring-1 focus:ring-emerald-500 focus:outline-none"
                  data-testid="event-minute-input"
                />
              </div>
              <Button onClick={confirmEvent} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-sm font-bold px-6" data-testid="confirm-event-btn">
                Confirm
              </Button>
              <Button variant="ghost" onClick={resetEventState} className="rounded-xl text-gray-400 hover:text-white text-sm" data-testid="cancel-event-btn">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

        {/* Change Formation Button */}
        <button
          onClick={() => setShowFormationPicker(prev => !prev)}
          className={`w-full mt-2 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
            showFormationPicker ? 'bg-emerald-600 text-white ring-2 ring-white/30' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          data-testid="change-formation-btn"
        >
          <Layers className="w-5 h-5" strokeWidth={2} />
          <span>Change Formation</span>
          <span className="text-xs opacity-60 ml-1">({match?.formation || '4-4-2'})</span>
        </button>

        {/* Formation Picker */}
        {showFormationPicker && (
          <div className="mt-2 bg-gray-900 rounded-xl p-3 border border-gray-800 animate-in slide-in-from-top-2" data-testid="formation-picker">
            <div className="flex flex-wrap gap-2">
              {(FORMATIONS_BY_FORMAT[match?.match_format || '11v11'] || FORMATIONS_BY_FORMAT['11v11']).map(f => (
                <button
                  key={f}
                  onClick={() => {
                    setMatch(prev => ({ ...prev, formation: f }));
                    setHasUnsavedChanges(true);
                    setShowFormationPicker(false);
                  }}
                  className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                    match?.formation === f
                      ? 'bg-emerald-600 text-white ring-1 ring-emerald-400'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  data-testid={`formation-option-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Penalty Shootout Button (cup matches only) */}
        {(match?.is_cup || match?.match_type === 'cup_knockout' || match?.match_type === 'cup_group') && (
          <button
            onClick={() => setShowShootout(prev => !prev)}
            className={`w-full mt-2 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
              showShootout ? 'bg-amber-600 text-white ring-2 ring-white/30' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="penalty-shootout-btn"
          >
            <CircleDot className="w-5 h-5" strokeWidth={2} />
            <span>Penalty Shootout</span>
            {penalties.length > 0 && (
              <span className="text-xs opacity-70 ml-1">({getShootoutScore().home} - {getShootoutScore().away})</span>
            )}
          </button>
        )}

        {/* Penalty Shootout Panel */}
        {showShootout && (match?.is_cup || match?.match_type === 'cup_knockout' || match?.match_type === 'cup_group') && (
          <div className="mt-2 bg-gray-900 rounded-xl p-3 border border-gray-800 space-y-3" data-testid="shootout-panel">
            {/* Shootout Score */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 py-2">
              <span className="text-xs sm:text-sm font-bold text-white truncate max-w-[28%] text-right">{user?.team_name || 'Home'}</span>
              <span className="text-2xl font-black tabular-nums">{getShootoutScore().home}</span>
              <span className="text-gray-600">-</span>
              <span className="text-2xl font-black tabular-nums">{getShootoutScore().away}</span>
              <span className="text-xs sm:text-sm font-bold text-gray-400 truncate max-w-[28%]">{match?.opponent}</span>
            </div>

            {/* Penalty history */}
            {penalties.length > 0 && (
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {penalties.map((pen, i) => {
                  const isHome = pen.team === 'home';
                  const penPlayer = pen.player_id ? getPlayer(pen.player_id) : null;
                  const penDisplay = pen.player_id ? getPlayerDisplay(pen.player_id) : null;
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs px-2 py-1.5 bg-gray-800 rounded-lg" data-testid={`shootout-pen-${i}`}>
                      <span className="text-gray-500 w-4 text-center">{Math.floor(i / 2) + 1}</span>
                      <span className={`font-semibold ${isHome ? 'text-white' : 'text-gray-400'}`}>
                        {isHome ? (penDisplay ? `#${penDisplay.number} ${penPlayer?.name?.split(' ').pop()}` : user?.team_name) : match?.opponent}
                      </span>
                      <span className="flex-1" />
                      <span className={`font-bold ${pen.result === 'scored' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pen.result === 'scored' ? 'Scored' : 'Missed'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Decided? */}
            {isShootoutDecided() ? (
              <div className="text-center py-3">
                <p className="text-lg font-bold text-amber-400">
                  {getShootoutScore().home > getShootoutScore().away ? (user?.team_name || 'Home') : match?.opponent} wins on penalties!
                </p>
                <p className="text-xs text-gray-500 mt-1">{getShootoutScore().home} - {getShootoutScore().away}</p>
              </div>
            ) : (
              <>
                {/* Next penalty input */}
                <div className="pt-1 border-t border-gray-800">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">
                    {nextPenTeam() === 'home' ? (user?.team_name || 'Home') : match?.opponent} — Penalty {Math.floor(penalties.length / 2) + 1}
                  </p>

                  {/* Home team: select player */}
                  {nextPenTeam() === 'home' && (
                    <div className="mb-2">
                      <div className="flex flex-wrap gap-1.5 ">
                        {getActiveLineup().filter(Boolean).map(pid => {
                          const p = getPlayerDisplay(pid);
                          return (
                            <button key={pid} onClick={() => setPenShootoutPlayer(pid)}
                              className={`px-2.5 py-2 rounded-lg text-xs font-semibold transition-all min-h-[36px] ${
                                penShootoutPlayer === pid ? 'bg-emerald-600 text-white ring-1 ring-emerald-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                              }`} data-testid={`shootout-player-${pid}`}>
                              #{p.number} {p.name?.split(' ').pop()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Scored / Missed buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { tapHaptic(); addPenalty(nextPenTeam(), nextPenTeam() === 'home' ? penShootoutPlayer : null, 'scored'); }}
                      disabled={nextPenTeam() === 'home' && !penShootoutPlayer}
                      className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="shootout-scored-btn"
                    >
                      Scored
                    </button>
                    <button
                      onClick={() => { tapHaptic(); addPenalty(nextPenTeam(), nextPenTeam() === 'home' ? penShootoutPlayer : null, 'missed'); }}
                      disabled={nextPenTeam() === 'home' && !penShootoutPlayer}
                      className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="shootout-missed-btn"
                    >
                      Missed
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Event Timeline Toggle (also VAR-scroll target) */}
      <div className="px-3 pt-4" id="event-timeline-section">
        <button
          onClick={() => setShowTimeline(prev => !prev)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-900 rounded-xl border border-gray-800 text-sm text-gray-300 hover:text-white transition-colors"
          data-testid="toggle-timeline-btn"
        >
          <span className="font-semibold inline-flex items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5 text-purple-400" strokeWidth={2.5} />
            VAR · Events ({events.length})
          </span>
          {showTimeline ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Event Timeline */}
      {showTimeline && (
        <div className="px-3 pt-2 pb-6" data-testid="event-timeline">
          {events.length === 0 ? (
            <div className="text-center py-6 text-gray-600 text-sm">No events logged yet</div>
          ) : (
            <div className="space-y-1.5">
              {events.map((event, idx) => {
                const player = event.player_id ? getPlayer(event.player_id) : null;
                const display = event.player_id ? getPlayerDisplay(event.player_id) : null;
                // Skip sub_out display — we show it with sub_in
                if (event.event_type === 'substitution_out') return null;
                // Skip standalone assist — we show it with the goal
                if (event.event_type === 'assist') return null;
                // Skip penalty_conceded — we show it with the goal
                if (event.event_type === 'penalty_conceded') return null;
                // For sub_in, also find the sub_out
                const subOut = event.event_type === 'substitution_in'
                  ? events.find(e => e.event_type === 'substitution_out' && e.minute === event.minute) : null;
                const subOutDisplay = subOut?.player_id ? getPlayerDisplay(subOut.player_id) : null;
                // For goals, find paired assist
                const pairedAssist = event.event_type === 'goal' && !event.is_opponent
                  ? events.find(e => e.event_type === 'assist' && e.minute === event.minute) : null;
                const assistDisplay = pairedAssist?.player_id ? getPlayerDisplay(pairedAssist.player_id) : null;
                const assistPlayerData = pairedAssist?.player_id ? getPlayer(pairedAssist.player_id) : null;

                return (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-900 rounded-lg border border-gray-800 text-sm" data-testid={`timeline-event-${idx}`}>
                    <span className="text-gray-500 font-mono text-xs w-8 text-right shrink-0">{event.minute}'</span>
                    <span className="text-base shrink-0">{getEventIcon(event.event_type)}</span>
                    <div className="flex-1 min-w-0 text-gray-200">
                      {event.event_type === 'own_goal' ? (
                        <span className="truncate block text-red-400">#{display?.number} {player?.name?.split(' ').pop()} <span className="text-xs opacity-70">(OG)</span></span>
                      ) : event.is_opponent ? (
                        <div>
                          <span className="text-red-400">{match.opponent}{event.is_penalty ? <span className="text-xs opacity-70"> (Pen)</span> : ''}</span>
                          {(() => {
                            const penConc = event.is_penalty ? events.find(e => e.event_type === 'penalty_conceded' && e.minute === event.minute) : null;
                            const penConcDisplay = penConc?.player_id ? getPlayerDisplay(penConc.player_id) : null;
                            const penConcPlayer = penConc?.player_id ? getPlayer(penConc.player_id) : null;
                            return penConcDisplay ? (
                              <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold bg-red-600 text-white leading-none">P</span> #{penConcDisplay.number} {penConcPlayer?.name?.split(' ').pop()}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      ) : event.event_type === 'substitution_in' ? (
                        <span className="truncate block">
                          <span className="text-emerald-400">#{display?.number} {player?.name?.split(' ').pop()}</span>
                          {subOutDisplay && <span className="text-gray-500"> for </span>}
                          {subOutDisplay && <span className="text-red-400">#{subOutDisplay.number} {getPlayer(subOut.player_id)?.name?.split(' ').pop()}</span>}
                        </span>
                      ) : event.event_type === 'goal' ? (
                        <div>
                          <span className="truncate block">
                            #{display?.number} {player?.name?.split(' ').pop()}
                            {event.goal_type && <span className="text-xs text-gray-500 ml-1">({
                              event.goal_type === 'penalty' ? 'Pen' :
                              event.goal_type === 'shot_inside' ? 'Inside Box' :
                              event.goal_type === 'shot_outside' ? 'Outside Box' :
                              event.goal_type === 'header' ? 'Header' :
                              event.goal_type === 'own_goal' ? 'OG' : ''
                            })</span>}
                          </span>
                          {assistDisplay && (
                            <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold bg-emerald-600 text-white leading-none">A</span> #{assistDisplay.number} {assistPlayerData?.name?.split(' ').pop()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="truncate block">#{display?.number} {player?.name?.split(' ').pop()}</span>
                      )}
                    </div>
                    <button onClick={() => startEditEvent(idx)} className="text-gray-600 hover:text-blue-400 transition-colors shrink-0 p-1" data-testid={`edit-event-${idx}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setDeleteEventIndex(idx)} className="text-gray-600 hover:text-red-400 transition-colors shrink-0 p-1" data-testid={`remove-event-${idx}`}>
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* End Match Button */}
      <div className="px-3 pt-6 pb-2">
        <Button 
          onClick={() => setShowEndDialog(true)}
          className="w-full rounded-xl font-bold text-sm py-6 bg-red-600 hover:bg-red-700 text-white gap-2"
          data-testid="end-match-btn"
        >
          <StopCircle className="w-5 h-5" strokeWidth={2} />
          End Match
        </Button>
      </div>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Unsaved Changes</DialogTitle>
            <DialogDescription className="text-gray-400">
              You have unsaved changes. Would you like to save before leaving?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={async () => {
              await handleSave();
              setShowUnsavedDialog(false);
              navigate(-1);
            }} disabled={saving} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="save-and-leave-btn">
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? 'Saving...' : 'Save & Leave'}
            </Button>
            <Button variant="outline" onClick={() => { setShowUnsavedDialog(false); navigate(-1); }} className="w-full rounded-xl border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800" data-testid="leave-without-saving-btn">
              Leave Without Saving
            </Button>
            <Button variant="ghost" onClick={() => setShowUnsavedDialog(false)} className="w-full rounded-xl text-gray-500 hover:text-white" data-testid="cancel-leave-btn">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* End Match Confirmation Dialog */}
      <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">End Match?</DialogTitle>
            <DialogDescription className="text-gray-400">
              This will save the final score ({ourScore} - {oppScore}) and all {events.length} events, 
              then close Match Day Mode. You won't be able to return to this live session.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowEndDialog(false)} className="flex-1 rounded-xl border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800" data-testid="cancel-end-match-btn">
              Continue Match
            </Button>
            <Button onClick={() => { setShowEndDialog(false); setShowPotmSelect(true); setSelectedPotm(null); }} disabled={saving} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="confirm-end-match-btn">
              <StopCircle className="w-4 h-4 mr-1.5" />
              End Match
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Player of the Match Selection */}
      <Dialog open={showPotmSelect} onOpenChange={(open) => { if (!open) setShowPotmSelect(false); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <Medal className="w-5 h-5 text-amber-400" /> Player of the Match
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Select your player of the match, or skip to add later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {/* Player grid */}
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
              {[...(match?.lineup || []), ...(match?.subs || []).map(s => s?.playerId)].filter(Boolean).map(pid => {
                const p = getPlayerDisplay(pid);
                const playerData = getPlayer(pid);
                const isSelected = selectedPotm === pid;
                // Count goals for this player in this match
                const goalCount = events.filter(e => e.event_type === 'goal' && e.player_id === pid && !e.is_opponent).length;
                const assistCount = events.filter(e => e.event_type === 'assist' && e.player_id === pid).length;
                return (
                  <button
                    key={pid}
                    onClick={() => setSelectedPotm(isSelected ? null : pid)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all ${
                      isSelected
                        ? 'bg-amber-500/20 border border-amber-500/50 ring-1 ring-amber-400/30'
                        : 'bg-gray-800 border border-gray-700/50 hover:bg-gray-750 hover:border-gray-600'
                    }`}
                    data-testid={`potm-player-${pid}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      isSelected ? 'bg-amber-400 text-gray-900' : 'bg-gray-700 text-white'
                    }`}>
                      {p.number}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold truncate ${isSelected ? 'text-amber-300' : 'text-white'}`}>
                        {playerData?.name?.split(' ').pop()}
                      </p>
                      {(goalCount > 0 || assistCount > 0) && (
                        <p className="text-[10px] text-gray-500">
                          {goalCount > 0 && `${goalCount}⚽`}{goalCount > 0 && assistCount > 0 && ' '}{assistCount > 0 && `${assistCount}A`}
                        </p>
                      )}
                    </div>
                    {isSelected && <Medal className="w-4 h-4 text-amber-400 shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-1">
              <Button
                onClick={() => handleEndMatch(selectedPotm)}
                disabled={saving}
                className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                data-testid="potm-confirm-btn"
              >
                {saving ? 'Saving...' : selectedPotm ? 'Save & End Match' : 'End Without POTM'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => handleEndMatch(null)}
                disabled={saving}
                className="w-full rounded-xl text-gray-500 hover:text-white text-sm"
                data-testid="potm-skip-btn"
              >
                Skip for now, add later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Event Confirmation Dialog */}
      <Dialog open={deleteEventIndex !== null} onOpenChange={(open) => { if (!open) setDeleteEventIndex(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Remove Event?</DialogTitle>
            <DialogDescription className="text-gray-400">
              {deleteEventIndex !== null && events[deleteEventIndex] && (() => {
                const evt = events[deleteEventIndex];
                const display = evt.player_id ? getPlayerDisplay(evt.player_id) : null;
                const label = evt.event_type === 'goal' ? 'Goal' :
                  evt.event_type === 'yellow_card' ? 'Yellow Card' :
                  evt.event_type === 'red_card' ? 'Red Card' :
                  evt.event_type === 'substitution_in' ? 'Substitution' :
                  evt.event_type === 'assist' ? 'Assist' :
                  evt.event_type?.replace('_', ' ');
                const who = evt.is_opponent ? match?.opponent : display ? `#${display.number} ${getPlayer(evt.player_id)?.name?.split(' ').pop() || ''}` : '';
                return `Are you sure you want to remove this event? ${label} ${who} at ${evt.minute}'`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setDeleteEventIndex(null)} className="flex-1 rounded-xl border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800" data-testid="cancel-delete-event-btn">
              Keep
            </Button>
            <Button onClick={() => { removeEvent(deleteEventIndex); setDeleteEventIndex(null); }} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="confirm-delete-event-btn">
              <Minus className="w-4 h-4 mr-1.5" />
              Remove
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editingEvent !== null} onOpenChange={(open) => { if (!open) setEditingEvent(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Edit Event</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update the details for this event.
            </DialogDescription>
          </DialogHeader>
          {editingEvent && (
            <div className="space-y-4 pt-2">
              {/* Event type label */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-base">
                  {editingEvent.event_type === 'goal' ? '⚽' :
                   editingEvent.event_type === 'yellow_card' ? '🟨' :
                   editingEvent.event_type === 'red_card' ? '🟥' :
                   editingEvent.event_type === 'substitution' ? '🔄' : '📋'}
                </span>
                <span className="font-semibold text-white capitalize">{editingEvent.event_type?.replace('_', ' ')}</span>
              </div>

              {/* Team toggle (goal/card only) */}
              {editingEvent.event_type !== 'substitution' && (
                <div className="flex rounded-lg bg-gray-800 p-0.5">
                  <button
                    onClick={() => setEditingEvent(prev => ({ ...prev, is_opponent: false }))}
                    className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${!editingEvent.is_opponent ? 'bg-emerald-600 text-white' : 'text-gray-400'}`}
                    data-testid="edit-our-team"
                  >
                    {user?.team_name || 'Our Team'}
                  </button>
                  <button
                    onClick={() => setEditingEvent(prev => ({ ...prev, is_opponent: true, player_id: null, assist_player_id: null }))}
                    className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${editingEvent.is_opponent ? 'bg-red-600 text-white' : 'text-gray-400'}`}
                    data-testid="edit-opponent"
                  >
                    {match?.opponent}
                  </button>
                </div>
              )}

              {/* Player selection (goal/card, our team) */}
              {editingEvent.event_type !== 'substitution' && !editingEvent.is_opponent && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Player:</p>
                  <div className="flex flex-wrap gap-1.5 ">
                    {getActiveLineup().filter(Boolean).map(pid => {
                      const p = getPlayerDisplay(pid);
                      return (
                        <button key={pid} onClick={() => setEditingEvent(prev => ({ ...prev, player_id: pid }))}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            editingEvent.player_id === pid ? 'bg-emerald-600 text-white ring-1 ring-emerald-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`} data-testid={`edit-select-player-${pid}`}>
                          #{p.number} {p.name?.split(' ').pop()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Assist selection (goal only, our team) */}
              {editingEvent.event_type === 'goal' && !editingEvent.is_opponent && editingEvent.player_id && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Assist <span className="normal-case font-normal">(optional)</span>:</p>
                  <div className="flex flex-wrap gap-1.5 ">
                    <button
                      onClick={() => setEditingEvent(prev => ({ ...prev, assist_player_id: null }))}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        !editingEvent.assist_player_id ? 'bg-gray-600 text-white ring-1 ring-gray-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                      data-testid="edit-assist-none"
                    >
                      No Assist
                    </button>
                    {getActiveLineup().filter(pid => pid && pid !== editingEvent.player_id).map(pid => {
                      const p = getPlayerDisplay(pid);
                      return (
                        <button key={pid} onClick={() => setEditingEvent(prev => ({ ...prev, assist_player_id: pid }))}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            editingEvent.assist_player_id === pid ? 'bg-blue-600 text-white ring-1 ring-blue-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`} data-testid={`edit-assist-player-${pid}`}>
                          #{p.number} {p.name?.split(' ').pop()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Substitution: player off */}
              {editingEvent.event_type === 'substitution' && (
                <>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Player Off:</p>
                    <div className="flex flex-wrap gap-1.5 ">
                      {getActiveLineup().filter(Boolean).map(pid => {
                        const p = getPlayerDisplay(pid);
                        return (
                          <button key={pid} onClick={() => setEditingEvent(prev => ({ ...prev, sub_off_id: pid }))}
                            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              editingEvent.sub_off_id === pid ? 'bg-red-600 text-white ring-1 ring-red-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                            }`} data-testid={`edit-sub-off-${pid}`}>
                            #{p.number} {p.name?.split(' ').pop()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Player On:</p>
                    <div className="flex flex-wrap gap-1.5 ">
                      {getAvailableBench().map(pid => {
                        const p = getPlayerDisplay(pid);
                        return (
                          <button key={pid} onClick={() => setEditingEvent(prev => ({ ...prev, sub_on_id: pid }))}
                            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              editingEvent.sub_on_id === pid ? 'bg-blue-600 text-white ring-1 ring-blue-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                            }`} data-testid={`edit-sub-on-${pid}`}>
                            #{p.number} {p.name?.split(' ').pop()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Minute */}
              <div className="flex items-center gap-2">
                <Timer className="w-4 h-4 text-gray-500" />
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={editingEvent.minute || ''}
                  onChange={(e) => setEditingEvent(prev => ({ ...prev, minute: e.target.value }))}
                  placeholder="Min"
                  className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-sm text-white text-center focus:ring-1 focus:ring-emerald-500 focus:outline-none"
                  data-testid="edit-event-minute"
                />
                <span className="text-xs text-gray-500">minutes</span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <Button variant="outline" onClick={() => setEditingEvent(null)} className="flex-1 rounded-xl border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800" data-testid="cancel-edit-event-btn">
                  Cancel
                </Button>
                <Button onClick={saveEditEvent} className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="save-edit-event-btn">
                  <Check className="w-4 h-4 mr-1.5" />
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Timer Start Confirmation */}
      <Dialog open={showTimerConfirm} onOpenChange={setShowTimerConfirm}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Start Timer?</DialogTitle>
            <DialogDescription className="text-gray-400">
              {matchPeriod === 'pre' && 'Ready to kick off the first half?'}
              {matchPeriod === 'half_time' && 'Ready to kick off the second half?'}
              {matchPeriod === 'et_half_time' && 'Ready to kick off ET 2nd Half?'}
              {!['pre','half_time','et_half_time'].includes(matchPeriod) && 'Resume the match timer?'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowTimerConfirm(false)} className="flex-1 rounded-xl border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800" data-testid="timer-cancel-btn">
              Cancel
            </Button>
            <Button onClick={() => {
              if (matchPeriod === 'pre') {
                setMatchPeriodByVoice('first_half');
              } else if (matchPeriod === 'half_time') {
                setMatchPeriodByVoice('second_half');
              } else if (matchPeriod === 'et_half_time') {
                setMatchPeriodByVoice('et_second');
              } else {
                startTimer();
              }
              setShowTimerConfirm(false);
            }} className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="timer-start-btn">
              <Timer className="w-4 h-4 mr-1.5" />
              Start
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timer Running Options */}
      <Dialog open={showTimerMenu} onOpenChange={setShowTimerMenu}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Match Timer</DialogTitle>
            <DialogDescription className="text-gray-400">
              {String(timerMinutes).padStart(2, '0')}:{String(timerSeconds).padStart(2, '0')} — What would you like to do?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            {(() => {
              const isCup = match?.is_cup || match?.match_type === 'cup_knockout' || match?.match_type === 'cup_group';
              const scoresTied = Number(ourScore) === Number(oppScore);
              const showEtOption = isCup && scoresTied && (matchPeriod === 'second_half' || matchPeriod === 'full_time');
              const showPenOption = isCup && scoresTied && matchPeriod === 'et_second';
              return (
                <>
                  {/* End current half */}
                  {matchPeriod === 'first_half' && (
                    <Button onClick={() => { setMatchPeriodByVoice('half_time'); toast.success('Half time'); setShowTimerMenu(false); }} className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold" data-testid="timer-end-half-btn">End First Half</Button>
                  )}
                  {matchPeriod === 'second_half' && (
                    <Button onClick={() => { setMatchPeriodByVoice('full_time'); toast.success('Full time'); setShowTimerMenu(false); }} className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold" data-testid="timer-end-half-btn">End Match (Full Time)</Button>
                  )}
                  {matchPeriod === 'et_first' && (
                    <Button onClick={() => { setMatchPeriodByVoice('et_half_time'); toast.success('ET half time'); setShowTimerMenu(false); }} className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold" data-testid="timer-end-et-first-btn">End ET 1st Half</Button>
                  )}
                  {matchPeriod === 'et_second' && (
                    <Button onClick={() => { setMatchPeriodByVoice('full_time'); toast.success('Full time'); setShowTimerMenu(false); }} className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold" data-testid="timer-end-et-second-btn">End ET 2nd Half</Button>
                  )}
                  {/* Cup tie-break options */}
                  {showEtOption && (
                    <Button onClick={() => { setMatchPeriodByVoice('et_first'); toast.success('Into Extra Time'); setShowTimerMenu(false); }} className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="timer-into-et-btn">
                      Into Extra Time
                    </Button>
                  )}
                  {showPenOption && (
                    <Button onClick={() => { setMatchPeriodByVoice('penalty'); toast.success('Penalty shoot-out'); setShowTimerMenu(false); }} className="w-full rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="timer-to-pens-btn">
                      To Penalties
                    </Button>
                  )}
                  {/* Always-available controls */}
                  <Button onClick={() => { stopTimer(); setShowTimerMenu(false); }} variant="outline" className="w-full rounded-xl border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800" data-testid="timer-pause-btn">
                    Pause
                  </Button>
                  <Button onClick={() => setShowTimerMenu(false)} variant="outline" className="w-full rounded-xl border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800" data-testid="timer-continue-btn">
                    Continue
                  </Button>
                  <Button onClick={() => {
                    stopTimer();
                    setTimerMinutes(0);
                    setTimerSeconds(0);
                    timerMinutesRef.current = 0;
                    timerSecondsRef.current = 0;
                    setMatchPeriod('pre');
                    matchPeriodRef.current = 'pre';
                    localStorage.removeItem(`matchday_timer_${matchId}`);
                    setShowTimerMenu(false);
                  }} variant="outline" className="w-full rounded-xl border-red-800 text-red-400 hover:text-red-300 hover:bg-red-900/30" data-testid="timer-reset-btn">
                    Reset
                  </Button>
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Voice Button */}
      {isSupported && user?.voice_control_enabled !== false && (
        <button
          {...voice.pressHandlers}
          disabled={voiceParsing || voice.isTranscribing}
          className={`fixed bottom-6 z-50 rounded-full p-4 shadow-lg transition-all duration-300 select-none touch-none ${
            user?.voice_button_position === 'left' ? 'left-6' : 'right-6'
          } ${
            isListening
              ? 'bg-red-500 hover:bg-red-600 scale-110 animate-pulse'
              : (voiceParsing || voice.isTranscribing)
                ? 'bg-[#007a33] opacity-70'
                : 'bg-[#007a33] hover:bg-[#005c26] hover:scale-110'
          }`}
          data-testid="matchday-voice-btn"
        >
          {(voiceParsing || voice.isTranscribing) ? (
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          ) : isListening ? (
            <MicOff className="w-6 h-6 text-white" />
          ) : (
            <Mic className="w-6 h-6 text-white" />
          )}
        </button>
      )}

      {/* Bottom padding for safe area on mobile */}
      <div className="h-20" />
    </div>
  );
}
